import * as Popover from "@radix-ui/react-popover";
import { memo } from "react";

import { SUGGESTIONS_PANEL_CLASS_NAME } from "./constants";
import type { IShareSuggestionsPanelProps } from "./types";

export const ShareSuggestionsPanel = memo(
  ({
    anchorRef,
    containerRef,
    children,
    label,
    portal,
    testId,
  }: IShareSuggestionsPanelProps): JSX.Element => {
    if (!portal) {
      return (
        <div className={SUGGESTIONS_PANEL_CLASS_NAME} data-testid={testId}>
          {children}
        </div>
      );
    }
    const { container, panelRef, onClose } = portal;

    // NOTE: NSAI-7565 — escape the dialog's scroll clipping. Radix measures the
    // available viewport space, flips the panel when needed, and tracks scrolling.
    return (
      <Popover.Root
        open
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
      >
        <Popover.Anchor virtualRef={anchorRef} />
        <Popover.Portal container={container}>
          <Popover.Content
            ref={panelRef}
            data-testid={testId}
            aria-label={label}
            align="start"
            sideOffset={4}
            collisionPadding={16}
            className="z-50 flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md outline-none"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onFocusOutside={(event) => {
              // NOTE: The search field stays outside the popover. Its nested
              // focus scope pauses the dialog's, so keep background autofocus
              // from stealing focus while still allowing the dialog's controls.
              event.preventDefault();
              if (
                event.target instanceof Node &&
                container &&
                !container.contains(event.target)
              ) {
                anchorRef.current
                  ?.querySelector<HTMLInputElement>("input")
                  ?.focus({ preventScroll: true });
              }
            }}
            onInteractOutside={(event) => {
              if (
                event.target instanceof Node &&
                containerRef.current?.contains(event.target)
              ) {
                event.preventDefault();
              }
            }}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  },
);

ShareSuggestionsPanel.displayName = "ShareSuggestionsPanel";
